"use client"

import { useQuery } from "@tanstack/react-query"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import { Badge } from "@/components/ui/badge"
import { getDailyRiseTop30 } from "@/lib/api/daily-rise-top30"
import type { BacktestWatchlistItem } from "@/types/backtest"

function fmtEok(n: number | null): string {
  if (n == null) return "-"
  return `${n.toLocaleString("ko-KR")}억원`
}

function fmtRate(n: number | null): string {
  if (n == null) return "-"
  return `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`
}

interface Props {
  selected: BacktestWatchlistItem | null
}

export function DailyRiseHistoryTab({ selected }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["dailyRiseTop30", selected?.stockCode],
    queryFn:  () => getDailyRiseTop30(selected!.stockCode),
    enabled:  !!selected,
  })

  if (!selected) {
    return (
      <div className="flex h-24 items-center justify-center text-xs text-zinc-500">
        종목을 선택하세요.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-16 w-full bg-zinc-800" />
        <Skeleton className="h-16 w-full bg-zinc-800" />
        <Skeleton className="h-16 w-full bg-zinc-800" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-24 items-center justify-center text-xs text-red-400">
        데이터를 불러올 수 없습니다.
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-24 items-center justify-center text-xs text-zinc-500">
        급등 이력 없음
      </div>
    )
  }

  return (
    <div className="space-y-1.5 p-4">
      {data.map((item) => (
        <div
          key={item.tradeDate}
          className="flex items-center gap-2 overflow-x-auto whitespace-nowrap rounded-lg border border-zinc-800 bg-zinc-800/40 px-3 py-2"
        >
          <span className="shrink-0 font-mono text-sm text-zinc-100">{item.tradeDate}</span>
          <span className={cn(
            "shrink-0 font-mono text-sm font-semibold",
            item.riseRate != null && item.riseRate > 0 ? "text-red-400" : "text-zinc-300"
          )}>
            {fmtRate(item.riseRate)}
          </span>
          <span className="shrink-0 font-mono text-xs text-zinc-400">거래대금 {fmtEok(item.tradeAmount)}</span>

          {item.riseReason && (
            <span className="text-xs text-zinc-300" title={item.riseReason}>
              {item.riseReason}
            </span>
          )}

          {item.themes.length > 0 && (
            <span className="flex shrink-0 items-center gap-1.5">
              <span className="text-xs text-zinc-500">테마</span>
              {item.themes.map((theme) => (
                <Badge key={theme} variant="outline" className="shrink-0 border-zinc-700 bg-zinc-800 text-zinc-300">
                  {theme}
                </Badge>
              ))}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
